import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface Stage {
  id: number;
  key: string;
  label: string;
  tone: string;
  description: string | null;
  is_won: boolean;
  is_lost: boolean;
}

interface Deal {
  id: number;
  name: string;
  company_id: number;
  primary_contact_id: number | null;
  stage: string;
  stage_position: number | null;
  value_pence: number | null;
  expected_close_date: string | null;
  won_at: string | null;
  lost_at: string | null;
  priority: number;
  notes: string | null;
  source: string;
  created_at: string;
  updated_at: string;
  company_name: string | null;
  company_domain: string | null;
  company_is_customer: boolean;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  last_demo_at: string | null;
  demo_count: number;
}

const TONE_CLASSES: Record<string, string> = {
  blue:   'bg-blue-50 border-blue-200',
  purple: 'bg-purple-50 border-purple-200',
  amber:  'bg-amber-50 border-amber-200',
  green:  'bg-green-50 border-green-200',
  red:    'bg-red-50 border-red-200',
  gray:   'bg-gray-100 border-gray-300',
  pink:   'bg-pink-50 border-pink-200',
  indigo: 'bg-indigo-50 border-indigo-200',
  teal:   'bg-teal-50 border-teal-200'
};

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatPence = (p: number | null): string => {
  if (p == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p / 100);
};
const formatDaysAgo = (iso: string | null): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

export const DealsView: React.FC<{ stages: Stage[] }> = ({ stages }) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      const res = await fetch(`/api/crm/deals?${qs}`, { headers: authHeaders() });
      const data = await res.json();
      setDeals(data.deals || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.company_name || '').toLowerCase().includes(q) ||
      (d.primary_contact_name || '').toLowerCase().includes(q)
    );
  }, [deals, search]);

  const byStage = useMemo(() => {
    const grouped: Record<string, Deal[]> = {};
    for (const s of stages) grouped[s.key] = [];
    for (const d of filtered) {
      if (!grouped[d.stage]) grouped[d.stage] = [];
      grouped[d.stage].push(d);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => {
        if ((b.priority || 0) !== (a.priority || 0)) return (b.priority || 0) - (a.priority || 0);
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    }
    return grouped;
  }, [filtered, stages]);

  const totals = useMemo(() => {
    const open  = deals.filter(d => !d.won_at && !d.lost_at);
    const won   = deals.filter(d => d.won_at);
    const lost  = deals.filter(d => d.lost_at);
    const sum   = (arr: Deal[]) => arr.reduce((acc, d) => acc + (d.value_pence || 0), 0);
    return { openCount: open.length, openValue: sum(open), wonCount: won.length, wonValue: sum(won), lostCount: lost.length };
  }, [deals]);

  // Drag-and-drop
  const onDragStart = (e: React.DragEvent, dealId: number) => {
    setDraggingId(dealId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(dealId));
  };
  const onDragOver = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };
  const onDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDraggingId(null);
    if (!id) return;
    const deal = deals.find(d => d.id === id);
    if (!deal || deal.stage === stageKey) return;

    // Optimistic — also clear won_at / lost_at locally if moving away from a terminal stage
    const targetStage = stages.find(s => s.key === stageKey);
    setDeals(prev => prev.map(d => d.id === id ? {
      ...d,
      stage: stageKey,
      won_at:  targetStage?.is_won  ? new Date().toISOString() : (targetStage?.is_lost ? null : null),
      lost_at: targetStage?.is_lost ? new Date().toISOString() : (targetStage?.is_won  ? null : null)
    } : d));

    try {
      const res = await fetch(`/api/crm/deals/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ stage: stageKey })
      });
      if (!res.ok) throw new Error('Stage update failed');
      const data = await res.json();
      setDeals(prev => prev.map(d => d.id === id ? data.deal : d));
    } catch (err) {
      load();
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center gap-3">
        <form onSubmit={e => { e.preventDefault(); load(); }} className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deals by name, company or contact…"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </form>
        <button onClick={() => setShowAdd(true)} className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
          + New deal
        </button>
        <button onClick={load} className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50">
          Refresh
        </button>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span>{totals.openCount} open ({formatPence(totals.openValue)})</span>
          <span className="text-green-700">{totals.wonCount} won ({formatPence(totals.wonValue)})</span>
          <span className="text-gray-400">{totals.lostCount} lost</span>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-lg shadow">Loading…</div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))` }}
        >
          {stages.map(stage => {
            const cards = byStage[stage.key] || [];
            const isDragOver = dragOverStage === stage.key;
            const colTotal = cards.reduce((acc, d) => acc + (d.value_pence || 0), 0);
            return (
              <div
                key={stage.key}
                onDragOver={e => onDragOver(e, stage.key)}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={e => onDrop(e, stage.key)}
                className={`rounded-lg border-2 transition-colors ${TONE_CLASSES[stage.tone] || TONE_CLASSES.gray} ${
                  isDragOver ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                }`}
              >
                <div className="px-3 py-2 border-b border-current/10 sticky top-0 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 text-sm">{stage.label}</h3>
                    <span className="text-xs text-gray-500 font-medium">{cards.length}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{formatPence(colTotal)}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[300px]">
                  {cards.map(d => (
                    <DealCard
                      key={d.id}
                      deal={d}
                      isDragging={draggingId === d.id}
                      onDragStart={onDragStart}
                      onClick={() => setEditingDeal(d)}
                    />
                  ))}
                  {cards.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-6 italic">
                      {isDragOver ? 'Drop here' : 'No deals'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-xs text-gray-500">
        Drag cards between columns to update stage. Won/Lost stamps automatically when you move to those columns.
      </div>

      {showAdd && (
        <DealModal
          stages={stages}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editingDeal && (
        <DealModal
          stages={stages}
          deal={editingDeal}
          onClose={() => setEditingDeal(null)}
          onSaved={() => { setEditingDeal(null); load(); }}
          onDeleted={() => { setEditingDeal(null); load(); }}
        />
      )}
    </div>
  );
};

// ============================================================
// Deal card
// ============================================================

const DealCard: React.FC<{
  deal: Deal;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, id: number) => void;
  onClick: () => void;
}> = ({ deal, isDragging, onDragStart, onClick }) => (
  <div
    draggable
    onDragStart={e => onDragStart(e, deal.id)}
    onClick={onClick}
    className={`bg-white rounded-md border border-gray-200 shadow-sm p-3 text-sm cursor-pointer hover:shadow-md transition-shadow ${
      isDragging ? 'opacity-50' : ''
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="font-semibold text-gray-900 truncate flex-1">{deal.name}</div>
      {deal.priority > 0 && (
        <span className="text-[10px] shrink-0" title={`Priority ${deal.priority}/5`}>
          {'🔥'.repeat(deal.priority)}
        </span>
      )}
    </div>
    {deal.company_name && (
      <div className="text-xs text-gray-700 truncate mt-0.5">
        {deal.company_name}
        {deal.company_is_customer && (
          <span className="ml-1 inline-block px-1 py-0.5 bg-green-100 text-green-700 rounded text-[9px] font-medium align-middle">Customer</span>
        )}
      </div>
    )}
    {deal.primary_contact_name && (
      <div className="text-xs text-gray-500 truncate mt-0.5">{deal.primary_contact_name}</div>
    )}
    <div className="flex items-center gap-2 mt-2 flex-wrap text-[10px]">
      {deal.value_pence != null && (
        <span className="font-semibold text-gray-900">{formatPence(deal.value_pence)}</span>
      )}
      {deal.last_demo_at && (
        <span className="text-gray-500">last demo {formatDaysAgo(deal.last_demo_at)}</span>
      )}
      {deal.demo_count > 0 && (
        <span className="text-gray-500">· {deal.demo_count} demo{deal.demo_count === 1 ? '' : 's'}</span>
      )}
    </div>
  </div>
);

// ============================================================
// Deal modal — create + edit
// ============================================================

const DealModal: React.FC<{
  stages: Stage[];
  deal?: Deal;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}> = ({ stages, deal, onClose, onSaved, onDeleted }) => {
  const isEdit = !!deal;
  const [name, setName] = useState(deal?.name || '');
  const [companyId, setCompanyId] = useState<string>(deal?.company_id ? String(deal.company_id) : '');
  const [companyName, setCompanyName] = useState(deal?.company_name || '');
  const [companySearch, setCompanySearch] = useState(deal?.company_name || '');
  const [companyResults, setCompanyResults] = useState<Array<{ id: number; name: string; domain: string | null }>>([]);
  const [stage, setStage] = useState(deal?.stage || stages[0]?.key || 'demo_booked');
  const [valuePounds, setValuePounds] = useState(deal?.value_pence != null ? String((deal.value_pence / 100).toFixed(2)) : '');
  const [closeDate, setCloseDate] = useState(deal?.expected_close_date?.slice(0, 10) || '');
  const [notes, setNotes] = useState(deal?.notes || '');
  const [priority, setPriority] = useState(deal?.priority || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Company picker — debounced search
  useEffect(() => {
    if (isEdit) return;
    if (!companySearch.trim() || companySearch === companyName) return;
    const t = setTimeout(async () => {
      const res = await fetch(`/api/crm/companies?search=${encodeURIComponent(companySearch.trim())}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCompanyResults((data.companies || []).slice(0, 8));
      }
    }, 200);
    return () => clearTimeout(t);
  }, [companySearch, companyName, isEdit]);

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Deal name required'); return; }
    if (!companyId) { setError('Company required'); return; }

    const body: any = {
      name: name.trim(),
      stage,
      priority,
      notes: notes.trim() || null,
      valuePence: valuePounds ? Math.round(parseFloat(valuePounds) * 100) : null,
      expectedCloseDate: closeDate || null
    };
    if (!isEdit) body.companyId = parseInt(companyId, 10);

    setSaving(true);
    try {
      const url = isEdit ? `/api/crm/deals/${deal!.id}` : '/api/crm/deals';
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Save failed');
      }
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!deal) return;
    if (!window.confirm(`Delete deal "${deal.name}"? This won't affect the company or contact.`)) return;
    await fetch(`/api/crm/deals/${deal.id}`, { method: 'DELETE', headers: authHeaders() });
    onDeleted?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit deal' : 'New deal'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800">{error}</div>}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Deal name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Q4 expansion — 20 monitoring seats"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>

          {/* Company picker — disabled for edits since changing it would re-key */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
            {isEdit ? (
              <input type="text" value={companyName} disabled
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-gray-50 text-gray-500" />
            ) : (
              <div className="relative">
                <input type="text" value={companySearch}
                  onChange={e => { setCompanySearch(e.target.value); setCompanyId(''); }}
                  placeholder="Type to search companies…"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                {companyResults.length > 0 && !companyId && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto">
                    {companyResults.map(c => (
                      <li key={c.id}>
                        <button type="button"
                          onClick={() => { setCompanyId(String(c.id)); setCompanyName(c.name); setCompanySearch(c.name); setCompanyResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                          <div className="font-medium text-gray-900">{c.name}</div>
                          {c.domain && <div className="text-xs text-gray-500">{c.domain}</div>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Stage</label>
              <select value={stage} onChange={e => setStage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Value (£)</label>
              <input type="number" step="0.01" min="0" value={valuePounds} onChange={e => setValuePounds(e.target.value)}
                placeholder="2400.00"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Expected close</label>
              <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority (0-5)</label>
              <input type="number" min="0" max="5" value={priority} onChange={e => setPriority(parseInt(e.target.value, 10) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>

          {isEdit && deal && (
            <div className="text-xs text-gray-500 border-t pt-2">
              <div>Created {new Date(deal.created_at).toLocaleDateString('en-GB')}</div>
              {deal.won_at  && <div className="text-green-700">Won {new Date(deal.won_at).toLocaleDateString('en-GB')}</div>}
              {deal.lost_at && <div className="text-gray-600">Lost {new Date(deal.lost_at).toLocaleDateString('en-GB')}</div>}
              {deal.company_id && (
                <div className="mt-1">
                  <Link to={`/admin/crm/companies/${deal.company_id}`} className="text-blue-600 hover:underline">
                    Open company →
                  </Link>
                  {deal.primary_contact_id && (
                    <>
                      {' · '}
                      <Link to={`/admin/crm/contacts/${deal.primary_contact_id}`} className="text-blue-600 hover:underline">
                        Open contact →
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center sticky bottom-0 bg-white">
          {isEdit ? (
            <button onClick={del} className="text-red-600 hover:text-red-800 text-sm">Delete deal</button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving || !name.trim() || (!isEdit && !companyId)}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
